import { apiClient } from '@/common/lib/api-client';

/**
 * Retrieve the list of WhatsApp conversations
 * GET /api/v1/whatsapp/companies/{company_id}/conversations/
 */
export async function getWhatsAppConversations(companyId, params = {}) {
  const { data } = await apiClient.get(
    `/v1/whatsapp/companies/${companyId}/conversations/`,
    { params },
  );
  return data;
}

/**
 * Retrieve details of a specific WhatsApp conversation
 * GET /api/v1/whatsapp/companies/{company_id}/conversations/{conversation_id}/
 */
export async function getWhatsAppConversationDetail({
  companyId,
  conversationId,
}) {
  const { data } = await apiClient.get(
    `/v1/whatsapp/companies/${companyId}/conversations/${conversationId}/`,
  );
  return data;
}

/**
 * Retrieve messages for a specific WhatsApp conversation
 * GET /api/v1/whatsapp/companies/{company_id}/conversations/{conversation_id}/messages/
 */
export async function getWhatsAppMessages({
  companyId,
  conversationId,
  params = {},
  pageParam,
  signal,
}) {
  const queryParams = { ...params };
  if (pageParam) {
    queryParams.page = pageParam;
  }
  const { data } = await apiClient.get(
    `/v1/whatsapp/companies/${companyId}/conversations/${conversationId}/messages/`,
    { params: queryParams, signal },
  );
  return data;
}

/**
 * Mark a WhatsApp conversation as read
 * POST /api/v1/whatsapp/companies/{company_id}/conversations/{conversation_id}/read/
 */
export async function markWhatsAppConversationRead({
  companyId,
  conversationId,
}) {
  const { data } = await apiClient.post(
    `/v1/whatsapp/companies/${companyId}/conversations/${conversationId}/read/`,
  );
  return data;
}

/**
 * Update a WhatsApp message locally
 * PATCH /api/v1/whatsapp/companies/{company_id}/messages/{message_id}/
 */
export async function updateWhatsAppMessage({ companyId, messageId, payload }) {
  const { data } = await apiClient.patch(
    `/v1/whatsapp/companies/${companyId}/messages/${messageId}/`,
    payload,
  );
  return data;
}

/**
 * Delete a WhatsApp message
 * DELETE /api/v1/whatsapp/companies/{company_id}/messages/{message_id}/
 */
export async function deleteWhatsAppMessage({ companyId, messageId }) {
  const { data } = await apiClient.delete(
    `/v1/whatsapp/companies/${companyId}/messages/${messageId}/`,
  );
  return data;
}

/**
 * Send a WhatsApp message
 * POST /api/v1/whatsapp/companies/{company_id}/messages/send/
 */
export async function sendWhatsAppMessage({ companyId, payload }) {
  const config = {};
  if (payload instanceof FormData) {
    config.headers = {
      'Content-Type': undefined,
    };
  }
  const { data } = await apiClient.post(
    `/v1/whatsapp/companies/${companyId}/messages/send/`,
    payload,
    config,
  );
  return data;
}

/**
 * Retrieve contact details & session history for a specific client
 * GET /v1/whatsapp/companies/{companyId}/clients/{clientId}/contact-details/
 */
export async function getWhatsAppContactDetails({ companyId, clientId }) {
  const { data } = await apiClient.get(
    `/v1/whatsapp/companies/${companyId}/clients/${clientId}/contact-details/`,
  );
  return data;
}

/**
 * React to a WhatsApp message
 * POST /api/v1/whatsapp/companies/{companyId}/messages/{messageId}/react/
 */
export async function reactToWhatsAppMessage({ companyId, messageId, emoji }) {
  const { data } = await apiClient.post(
    `/v1/whatsapp/companies/${companyId}/messages/${messageId}/react/`,
    { emoji },
  );
  return data;
}

// ==================== INSTAGRAM INBOX API ====================

/**
 * Retrieve the list of Instagram conversations
 * GET /v1/instagram/companies/{company_id}/conversations/
 */
export async function getInstagramConversations(companyId, params = {}) {
  const { data } = await apiClient.get(
    `/v1/instagram/companies/${companyId}/conversations/`,
    { params },
  );
  return data;
}

/**
 * Retrieve details of a specific Instagram conversation
 * GET /v1/instagram/companies/{company_id}/conversations/{conversation_id}/
 */
export async function getInstagramConversationDetail({
  companyId,
  conversationId,
}) {
  const { data } = await apiClient.get(
    `/v1/instagram/companies/${companyId}/conversations/${conversationId}/`,
  );
  return data;
}

/**
 * Retrieve messages for a specific Instagram conversation
 * GET /v1/instagram/companies/{company_id}/conversations/{conversation_id}/messages/
 */
export async function getInstagramMessages({
  companyId,
  conversationId,
  params = {},
  pageParam,
  signal,
}) {
  const queryParams = { ...params };
  if (pageParam) {
    queryParams.page = pageParam;
  }
  const { data } = await apiClient.get(
    `/v1/instagram/companies/${companyId}/conversations/${conversationId}/messages/`,
    { params: queryParams, signal },
  );
  return data;
}

/**
 * Mark an Instagram conversation as read
 * POST /v1/instagram/companies/{company_id}/conversations/{conversation_id}/read/
 */
export async function markInstagramConversationRead({
  companyId,
  conversationId,
}) {
  const { data } = await apiClient.post(
    `/v1/instagram/companies/${companyId}/conversations/${conversationId}/read/`,
  );
  return data;
}

/**
 * Send an Instagram DM message
 * POST /v1/instagram/companies/{company_id}/messages/send/
 */
export async function sendInstagramMessage({ companyId, payload }) {
  const config = {};
  if (payload instanceof FormData) {
    config.headers = {
      'Content-Type': undefined,
    };
  }
  const { data } = await apiClient.post(
    `/v1/instagram/companies/${companyId}/messages/send/`,
    payload,
    config,
  );
  return data;
}

/**
 * React to an Instagram message
 * POST /v1/instagram/companies/{company_id}/messages/{message_id}/react/
 */
export async function reactToInstagramMessage({ companyId, messageId, emoji }) {
  const { data } = await apiClient.post(
    `/v1/instagram/companies/${companyId}/messages/${messageId}/react/`,
    { emoji },
  );
  return data;
}

// ==================== GMAIL / EMAIL INBOX API ====================

/**
 * Retrieve Gmail inbox messages for a company
 * GET /v1/google-workspace/companies/{company_id}/gmail/messages/
 */
export async function getGmailMessages(companyId, params = {}) {
  const { data } = await apiClient.get(
    `/v1/google-workspace/companies/${companyId}/gmail/messages/`,
    { params },
  );
  return data;
}

/**
 * Send an email via Gmail API
 * POST /v1/google-workspace/companies/{company_id}/gmail/messages/send/
 */
export async function sendGmailMessage({ companyId, payload }) {
  const { data } = await apiClient.post(
    `/v1/google-workspace/companies/${companyId}/gmail/messages/send/`,
    payload,
  );
  return data;
}

/**
 * Retrieve all messages in a Gmail thread
 * GET /v1/google-workspace/companies/{company_id}/gmail/threads/{thread_id}/
 */
export async function getGmailThreadMessages({ companyId, threadId }) {
  const { data } = await apiClient.get(
    `/v1/google-workspace/companies/${companyId}/gmail/threads/${threadId}/`,
  );
  return data;
}

/**
 * Mark a Gmail thread as read
 * POST /v1/google-workspace/companies/{company_id}/gmail/threads/{thread_id}/read/
 */
export async function markGmailThreadRead({ companyId, threadId }) {
  const { data } = await apiClient.post(
    `/v1/google-workspace/companies/${companyId}/gmail/threads/${threadId}/read/`,
  );
  return data;
}

/**
 * Start or resume a Gmail sync
 * POST /v1/google-workspace/companies/{company_id}/gmail/sync/
 */
export async function startGmailSync({ companyId, mode = 'all' }) {
  const { data } = await apiClient.post(
    `/v1/google-workspace/companies/${companyId}/gmail/sync/`,
    { mode },
  );
  return data;
}

/**
 * Create or link lead from a Gmail thread
 * POST /v1/google-workspace/companies/{company_id}/gmail/threads/{thread_id}/create-lead/
 */
export async function createLeadFromGmailThread({
  companyId,
  threadId,
  payload = {},
}) {
  const { data } = await apiClient.post(
    `/v1/google-workspace/companies/${companyId}/gmail/threads/${threadId}/create-lead/`,
    payload,
  );
  return data;
}

/**
 * Get email threads linked to a lead
 * GET /v1/google-workspace/companies/{company_id}/gmail/leads/{lead_id}/threads/
 */
export async function getGmailLeadThreads({ companyId, leadId }) {
  const { data } = await apiClient.get(
    `/v1/google-workspace/companies/${companyId}/gmail/leads/${leadId}/threads/`,
  );
  return data;
}

/**
 * Get Gmail sync status
 * GET /v1/google-workspace/companies/{company_id}/gmail/sync/status/
 */
export async function getGmailSyncStatus(companyId) {
  const { data } = await apiClient.get(
    `/v1/google-workspace/companies/${companyId}/gmail/sync/status/`,
  );
  return data;
}

/**
 * Retrieve email templates for a company
 * GET /v1/google-workspace/companies/{company_id}/gmail/templates/
 */
export async function getGmailTemplates(companyId) {
  const { data } = await apiClient.get(
    `/v1/google-workspace/companies/${companyId}/gmail/templates/`,
  );
  return data;
}

/**
 * Create a new email template
 * POST /v1/google-workspace/companies/{company_id}/gmail/templates/
 */
export async function createGmailTemplate({ companyId, payload }) {
  const { data } = await apiClient.post(
    `/v1/google-workspace/companies/${companyId}/gmail/templates/`,
    payload,
  );
  return data;
}

/**
 * Retrieve bulk email campaigns
 * GET /v1/google-workspace/companies/{company_id}/gmail/campaigns/
 */
export async function getGmailBulkCampaigns(companyId) {
  const { data } = await apiClient.get(
    `/v1/google-workspace/companies/${companyId}/gmail/campaigns/`,
  );
  return data;
}

/**
 * Create a new bulk email campaign
 * POST /v1/google-workspace/companies/{company_id}/gmail/campaigns/
 */
export async function createGmailBulkCampaign({ companyId, payload }) {
  const { data } = await apiClient.post(
    `/v1/google-workspace/companies/${companyId}/gmail/campaigns/`,
    payload,
  );
  return data;
}

/**
 * Retrieve Gmail categories
 * GET /v1/google-workspace/companies/{company_id}/gmail/categories/
 */
export async function getGmailCategories(companyId) {
  const { data } = await apiClient.get(
    `/v1/google-workspace/companies/${companyId}/gmail/categories/`,
  );
  return data;
}
